<script lang="ts" setup>
import { useDebounce } from "@vueuse/core"
import { ChevronDown } from "lucide-vue-next"
import { PopoverArrow } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { computed, nextTick, ref } from "vue"
import { ThemeButton } from "~/components/theme"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover"
import { useScrolled } from "~/composables/scroll"
import { cn } from "~/lib/utils"
import { NAV_DEBOUNCE_MS, NavItem, NavLink, NavMenuVariants } from "."

const { item, variant, class: cl } = defineProps<{
    item: NavItem
    variant?: NavMenuVariants["variant"]
    class?: HTMLAttributes["class"]
}>()

const emits = defineEmits<{
    (e: "select", sectionId?: string): void
}>()

const scrolled = useScrolled()

const open = ref(false)
const debouncedOpen = useDebounce(open, NAV_DEBOUNCE_MS)
const itemOpen = ref(false)
const triggerRef = ref<{ $el: HTMLElement } | null>(null)

const ariaLabel = computed(() => {
    return `Open ${item.name} menu`
})

const itemClass = computed(() => {
    return cn(
        !item.button && "text-foreground uppercase",
        !item.button && variant === "vertical" && "px-0",
        item.button && !item.emphasize && "border-foreground bg-transparent hover:bg-foreground hover:text-accent",
        scrolled.value && item.emphasize && "bg-foreground text-accent hover:bg-foreground/80"
    )
})

function handleMouseEnter() {
    if (!itemOpen.value) {
        open.value = true
    }
}

function handleMouseLeave() {
    if (!itemOpen.value) {
        open.value = false
    }
}

function handleSelectItem(sectionId?: string) {
    open.value = false
    emits("select", sectionId)
}

function handleOpenItem() {
    itemOpen.value = true
}

function handleCloseItem() {
    itemOpen.value = false
    open.value = false
    emits("select")
}

function handleKeydown(e: KeyboardEvent, index: number) {
    const isFirst = index === 0
    const isLast = index === (item.items?.length ?? 0) - 1

    if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        open.value = false
        nextTick(() => {
            triggerRef.value?.$el?.focus()
        })
    } else if ((e.key === "Tab" && !e.shiftKey && isLast) || (e.key === "Tab" && e.shiftKey && isFirst)) {
        e.preventDefault()
        e.stopPropagation()
        triggerRef.value?.$el?.focus()
        open.value = false
    } else if (e.key === "Enter") {
        e.preventDefault()
        ;(e.target as HTMLElement).click()
        open.value = false
    }
}

function handleEscapeKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
        e.preventDefault()
        open.value = false
        nextTick(() => {
            triggerRef.value?.$el?.focus()
        })
    }
}
</script>

<template>
    <Popover v-model:open="debouncedOpen">
        <PopoverTrigger
            as-child
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
        >
            <ThemeButton
                ref="triggerRef"
                :variant="!item.button ? 'link' : item.emphasize ? 'default' : 'outline'"
                size="sm"
                :class="cn(itemClass, cl)"
                tabindex="0"
                aria-haspopup="true"
                :aria-expanded="debouncedOpen ? 'true' : 'false'"
                :aria-label="ariaLabel"
                @click="open = !open"
            >
                <span class="capsize">{{ item.name }}</span>
                <ChevronDown :class="cn('size-4 transform transition-transform ease-in-out', debouncedOpen && 'rotate-180')" />
            </ThemeButton>
        </PopoverTrigger>
        <PopoverContent
            align="start"
            class="w-72 p-2 border-none"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            @escapeKeyDown="handleEscapeKeyDown"
        >
            <PopoverArrow class="fill-muted" />
            <ul class="w-full">
                <li
                    v-for="(subItem, index) of item.items"
                    :key="subItem.name"
                    class="flex"
                >
                    <NavLink
                        :item="subItem"
                        :variant="variant"
                        class="text-nav-bar-items-dropdown"
                        tabindex="0"
                        @select="handleSelectItem"
                        @open="handleOpenItem"
                        @close="handleCloseItem"
                        @keydown="handleKeydown($event, index)"
                    />
                </li>
            </ul>
        </PopoverContent>
    </Popover>
</template>
